import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../utils/formatters";
import { getShortName } from "../utils/productNames";

export default function WishlistPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { wishlist, removeFromWishlist, loading } = useWishlist();
  const { addToCart } = useCart();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center" data-testid="wishlist-login-prompt">
        <div className="text-center">
          <Heart className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
          <h2 className="font-heading text-3xl mb-4">LOGIN TO VIEW WISHLIST</h2>
          <p className="text-[#A3A3A3] mb-8">Please login to access your wishlist</p>
          <Link to="/auth" className="btn-primary inline-block" data-testid="login-btn">
            Login
          </Link>
        </div>
      </div>
    );
  }

  const handleRemove = async (productId) => {
    await removeFromWishlist(productId);
    toast.success("Removed from wishlist");
  };

  const handleAddToCart = async (product) => {
    if (!product.sizes?.length || !product.colors?.length) {
      toast.error("Product configuration missing");
      return;
    }

    const success = await addToCart(
      product.id,
      1,
      product.sizes[0],
      product.colors[0]
    );

    if (success) {
      toast.success("Added to cart!");
    } else {
      toast.error("Failed to add to cart");
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="wishlist-page">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">
            YOUR FAVORITES
          </p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">
            WISHLIST
          </h1>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loader" />
          </div>
        ) : wishlist.items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Heart className="w-16 h-16 mx-auto text-[#A3A3A3] mb-6" />
            <h2 className="font-heading text-2xl mb-4">YOUR WISHLIST IS EMPTY</h2>
            <p className="text-[#A3A3A3] mb-8">Save your favorite items here</p>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-3" data-testid="browse-products-btn">
              Browse Products
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {wishlist.items.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`wishlist-item-${product.id}`}
              >
                <div className="card-brutalist overflow-hidden relative">
                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(product.id)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-red-500 hover:border-red-500 transition-colors"
                    data-testid={`remove-wishlist-${product.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>

                  {/* Image */}
                  <Link to={`/product/${product.id}`}>
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={product.images?.[0]}
                        alt={product.name}
                        className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </Link>
                </div>

                {/* Info */}
                <div className="mt-4 space-y-2">
                  <Link to={`/product/${product.id}`}>
                    <h3 className="font-heading text-xl tracking-wider group-hover:text-[#FFD700] transition-colors">
                      {getShortName(product.name)}
                    </h3>
                  </Link>
                  <p className="font-mono text-[#FFD700] text-lg">{formatPrice(product.price)}</p>
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="btn-outline w-full flex items-center justify-center gap-2 mt-4 py-3"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingBag className="w-4 h-4" />
                    Add to Cart
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
